/**
 * Хронум для Экономиста — логика экрана без React (ТЗ AI-Economist-2026 ред. 2.1,
 * п. 6.4, 6.8, 7.4). Что приходит из Хронума и чем закончилась загрузка, месяцы,
 * часы строками без float, понятные формулировки аномалий свода, история
 * загрузок, журнал правок сопоставлений.
 *
 * Модуль намеренно без импортов значений (только `import type`): его проверяет
 * `model.test.mjs` на встроенном node:test, а Node не знает путей-алиасов.
 */
import type {
  ChronumCostCenter,
  ChronumDataQuality,
  ChronumHeadcount,
  ChronumHours,
  ChronumHoursAnomalies,
  ChronumHoursObject,
  ChronumPayrollAnomalies,
  ChronumPayrollObject,
  ChronumLoad,
  ChronumLoadCounts,
  ChronumLoadResult,
  ChronumLoadStatus,
  ChronumLoadWithSnapshots,
  ChronumMapRevision,
  ChronumPayrollCategory,
  ChronumPeriodState,
  ChronumPosition,
  ChronumSite,
  ChronumSiteBinding,
  ChronumSnapshotMeta,
  ChronumTarget,
} from '@shared/api';

export const NBSP = String.fromCharCode(0xa0);
const MINUS = String.fromCharCode(0x2212);
/** WORD JOINER: «чел.-ч» и «маш.-ч» не рвутся на дефисе. */
const WJ = String.fromCharCode(0x2060);
export const MAN_HOURS = `чел.-${WJ}ч`;
export const MACHINE_HOURS = `маш.-${WJ}ч`;

// ── Типографика и склонение ────────────────────────────────────────────────

/** Короткие слова, после которых строка не должна обрываться. */
const SHORT_WORD =
  /(?<=^|[\s(«„"])(а|без|в|во|для|до|и|из|к|ко|на|над|не|ни|о|об|от|по|под|при|с|со|у|за)\s+/giu;

/**
 * Неразрывные пробелы для русского текста: после коротких слов, перед тире и
 * между числом и словом («30 с», «40 ч», «3 человека»).
 */
export function typo(text: string): string {
  return text
    .replace(SHORT_WORD, `$1${NBSP}`)
    .replace(/\s+—/g, `${NBSP}—`)
    .replace(/(\d) (?=[\p{L}%])/gu, `$1${NBSP}`);
}

/** Форма слова по числу: `plural(2, ['площадка', 'площадки', 'площадок'])`. */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

const PEOPLE = ['человек', 'человека', 'человек'] as const;

/** «3 человека». */
export function people(n: number): string {
  return `${formatInt(n)}${NBSP}${plural(n, PEOPLE)}`;
}

// ── Месяцы ─────────────────────────────────────────────────────────────────

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
] as const;

const PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Месяц `ГГГГ-ММ`. */
export function isPeriod(value: string | null | undefined): value is string {
  return typeof value === 'string' && PERIOD_RE.test(value);
}

/** Месяц даты по часам пользователя: `2026-10`. */
export function periodOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Сдвиг месяца: `shiftPeriod('2026-01', -1)` → `2025-12`. */
export function shiftPeriod(period: string, delta: number): string {
  const m = PERIOD_RE.exec(period);
  if (!m) return period;
  const index = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** «Октябрь 2026». */
export function periodLabel(period: string): string {
  const m = PERIOD_RE.exec(period);
  if (!m) return period;
  const name = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}${NBSP}${m[1]}`;
}

/** «октябрь 2026» — для «за октябрь 2026». */
export function periodLabelLower(period: string): string {
  const m = PERIOD_RE.exec(period);
  if (!m) return period;
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]}${NBSP}${m[1]}`;
}

/** Месяц снимка: `2026-10` или `2026-10-01` → `2026-10`; `null` — у ручки нет периода. */
export function periodFromDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const p = iso.slice(0, 7);
  return isPeriod(p) ? p : null;
}

/** Месяцы для выбора: от `latest` назад, `count` штук, свежие сверху. */
export function periodOptions(latest: string, count: number): string[] {
  return Array.from({ length: Math.max(1, count) }, (_, i) => shiftPeriod(latest, -i));
}

// ── Даты ───────────────────────────────────────────────────────────────────

/** «20.10.2026, 14:02» по часам пользователя. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** «03.11.2026». */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** «14:02» по часам пользователя. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Сколько идёт загрузка: «42 с», «2 мин 05 с». */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}${NBSP}с`;
  const m = Math.floor(s / 60);
  return `${m}${NBSP}мин ${String(s % 60).padStart(2, '0')}${NBSP}с`;
}

// ── Числа строками ─────────────────────────────────────────────────────────

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/** Число из ответа (строка «1395.9» или число JSON) → строка-число или `null`. */
export function decimalOf(raw: unknown): string | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    const s = String(raw);
    return /e/i.test(s)
      ? raw.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
      : s;
  }
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  return DECIMAL_RE.test(s) ? s : null;
}

interface Scaled {
  digits: bigint;
  scale: number;
}

function parseScaled(raw: string): Scaled | null {
  const m = DECIMAL_RE.exec(raw.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const digits = BigInt(`${m[2]}${frac}`);
  return { digits: m[1] ? -digits : digits, scale: frac.length };
}

function scaledText({ digits, scale }: Scaled): string {
  const negative = digits < BigInt(0);
  let s = (negative ? -digits : digits).toString();
  if (scale > 0) {
    s = s.padStart(scale + 1, '0');
    const frac = s.slice(-scale).replace(/0+$/, '');
    s = `${s.slice(0, -scale)}${frac ? `.${frac}` : ''}`;
  }
  return negative && s !== '0' ? `-${s}` : s;
}

/**
 * Сумма чисел-строк без float: `addDecimals(['1551', '368.5'])` → `1919.5`.
 * Не числа пропускаются; пустой список — `0`.
 */
export function addDecimals(values: readonly (string | null | undefined)[]): string {
  const parsed: Scaled[] = [];
  let scale = 0;
  for (const v of values) {
    const p = typeof v === 'string' ? parseScaled(v) : null;
    if (!p) continue;
    parsed.push(p);
    scale = Math.max(scale, p.scale);
  }
  let sum = BigInt(0);
  for (const p of parsed) sum += p.digits * BigInt(10) ** BigInt(scale - p.scale);
  return scaledText({ digits: sum, scale });
}

/** Больше нуля ли число-строка. */
export function isPositiveDecimal(raw: string | null | undefined): boolean {
  const p = typeof raw === 'string' ? parseScaled(raw) : null;
  return p !== null && p.digits > BigInt(0);
}

/**
 * Число-строка → «1 395,9»: разряды неразрывным пробелом, запятая, не больше
 * `maxFraction` знаков (округление половины вверх — на строке).
 */
export function formatDecimal(raw: string, maxFraction: number): string | null {
  const m = DECIMAL_RE.exec(raw.trim());
  if (!m) return null;
  let int = m[2];
  let frac = m[3] ?? '';
  if (frac.length > maxFraction) {
    const digits = `${int}${frac.slice(0, maxFraction)}`.split('').map(Number);
    if (Number(frac.charAt(maxFraction)) >= 5) {
      let i = digits.length - 1;
      while (i >= 0 && digits[i] === 9) {
        digits[i] = 0;
        i -= 1;
      }
      if (i >= 0) digits[i] += 1;
      else digits.unshift(1);
    }
    const all = digits.join('');
    int = all.slice(0, all.length - maxFraction);
    frac = all.slice(all.length - maxFraction);
  }
  int = int.replace(/^0+(?=\d)/, '') || '0';
  frac = frac.replace(/0+$/, '');
  const negative = m[1] === '-' && /[1-9]/.test(int + frac);
  const body = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${negative ? MINUS : ''}${body}${frac ? `,${frac}` : ''}`;
}

/** Часы: «1 395,9»; нет значения — «—». */
export function formatHours(raw: string | number | null | undefined): string {
  const s = decimalOf(raw);
  if (s === null) return '—';
  return formatDecimal(s, 2) ?? s;
}

/** Целое с разрядами: «1 234». */
export function formatInt(n: number): string {
  return formatDecimal(String(Math.trunc(n)), 0) ?? String(n);
}

/** Процент: «13,44 %». */
export function formatPercent(raw: string | null | undefined): string {
  const s = decimalOf(raw);
  if (s === null) return '—';
  return `${formatDecimal(s, 2) ?? s}${NBSP}%`;
}

/** Коэффициент: «0,9». */
export function formatCoef(raw: string | null | undefined): string {
  const s = decimalOf(raw);
  if (s === null) return '—';
  return formatDecimal(s, 4) ?? s;
}

// ── Что приходит из Хронума ────────────────────────────────────────────────

export type ChronumTone = 'success' | 'info' | 'neutral' | 'danger' | 'warning' | 'muted';

/** Ключ данных: ручка, у часов — с основанием (`hours:credited`). */
export type ChronumFeedKey =
  | 'hours:credited'
  | 'hours:marks'
  | 'positions'
  | 'sites'
  | 'periods'
  | 'labor_cost'
  | 'rates'
  | 'block5';

export interface ChronumFeed {
  key: ChronumFeedKey;
  label: string;
  /** Зачем эти данные — одной фразой. */
  hint: string;
  /** Загружаются за месяц (иначе — справочник без периода). */
  periodic: boolean;
}

/** Данные Хронума в порядке важности для свода месяца. */
export const CHRONUM_FEEDS: readonly ChronumFeed[] = [
  {
    key: 'hours:credited',
    label: 'Часы по табелю',
    hint: 'Часы табеля после правок — из них собирается свод месяца.',
    periodic: true,
  },
  {
    key: 'hours:marks',
    label: 'Часы по отметкам',
    hint: 'Часы по отметкам терминалов — для сверки с табелем.',
    periodic: true,
  },
  {
    key: 'positions',
    label: 'Должности',
    hint: 'Должности компаний холдинга — для категорий ФОТ и групп механизаторов.',
    periodic: false,
  },
  {
    key: 'sites',
    label: 'Площадки',
    hint: 'Площадки — для привязки к стройкам и центрам затрат.',
    periodic: false,
  },
  {
    key: 'periods',
    label: 'Закрытие месяцев',
    hint: 'Закрыт ли месяц в Хронуме: окончательные данные или предварительные.',
    periodic: false,
  },
  {
    key: 'labor_cost',
    label: 'Начисленный ФОТ',
    hint: 'Начисленная зарплата по площадкам и должностям, без ФИО.',
    periodic: true,
  },
  {
    key: 'rates',
    label: 'Средние ставки',
    hint: 'Средние ставки должностей — для расчёта «часы × ставка».',
    periodic: true,
  },
  {
    key: 'block5',
    label: 'Блок 5',
    hint: 'Сдельная оплата водителей и механизаторов: смены, рейсы, километры.',
    periodic: true,
  },
];

const FEED_BY_KEY = new Map<string, ChronumFeed>(CHRONUM_FEEDS.map((f) => [f.key, f]));
const FEED_ORDER = new Map<string, number>(CHRONUM_FEEDS.map((f, i) => [f.key, i]));

/**
 * Ключ данных: `hours` + основание, остальные — по ручке. Основание — строкой
 * (`basis` снимка и итога загрузки) или в параметрах снимка (`params.basis`).
 */
export function feedKey(
  endpoint: string,
  basis: string | Record<string, unknown> | null | undefined,
): string {
  const b = typeof basis === 'string' ? basis : basis?.basis;
  return typeof b === 'string' && b ? `${endpoint}:${b}` : endpoint;
}

export function feedOf(key: string): ChronumFeed | undefined {
  return FEED_BY_KEY.get(key);
}

/** Подпись данных снимка; неизвестная ручка — общей фразой, без кода. */
export function feedLabel(key: string): string {
  return FEED_BY_KEY.get(key)?.label ?? 'Другие данные Хронума';
}

function feedOrder(key: string): number {
  return FEED_ORDER.get(key) ?? CHRONUM_FEEDS.length;
}

export function snapshotStatusLabel(status: string): string {
  switch (status) {
    case 'ok':
      return 'Загружено';
    case 'not_available':
      return 'Хронум пока не отдаёт';
    case 'denied':
      return 'Нет прав у ключа';
    case 'error':
      return 'Сбой загрузки';
    default:
      return 'Итог неизвестен';
  }
}

/** «Ручки ещё нет» — нейтрально: Хронум её делает, это не ошибка. */
export function snapshotStatusTone(status: string): ChronumTone {
  switch (status) {
    case 'ok':
      return 'success';
    case 'not_available':
      return 'neutral';
    case 'denied':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'muted';
  }
}

/** Служебные префиксы текста ошибки сервера («upstream error: …»). */
const ERROR_PREFIX =
  /^(not found|conflict|validation|bad request|upstream error|service unavailable|database error|internal error):\s*/i;

/** Причина сбоя для человека: без служебного префикса, с заглавной буквы. */
export function failureReason(message: string | null | undefined): string | null {
  const text = (message ?? '').replace(ERROR_PREFIX, '').trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : null;
}

/**
 * Пояснение к итогу: что это значит и кто может помочь. `short` — коротко,
 * для строки таблицы рядом с меткой итога.
 */
export function snapshotStatusHint(
  status: string,
  message: string | null | undefined,
  { short = false }: { short?: boolean } = {},
): string | null {
  switch (status) {
    case 'not_available':
      return short
        ? 'Выгрузку ещё делают в Хронуме — это не ошибка.'
        : 'Эти данные в Хронуме ещё делают — появятся при следующей загрузке, когда Хронум начнёт их отдавать.';
    case 'denied':
      return short
        ? 'Права ключу выдаёт администратор Хронума.'
        : 'Ключу Экономиста в Хронуме не хватает прав — это настраивает администратор Хронума.';
    case 'error': {
      const reason = failureReason(message);
      return reason ? `Причина: ${reason}` : 'Хронум не ответил — повторите загрузку позже.';
    }
    default:
      return null;
  }
}

/**
 * Кто загрузил: ФИО, «вручную» или «по расписанию». Автоматическая загрузка
 * идёт не ночью, а через заданный интервал от запуска сервера.
 */
export function originLabel(meta: Pick<ChronumSnapshotMeta, 'origin' | 'fetched_by_name'>): string {
  if (meta.origin === 'schedule') return 'по расписанию';
  return meta.fetched_by_name?.trim() || 'вручную';
}

/** Кто начал загрузку: ФИО, «вручную» или «по расписанию». */
export function loadByLabel(load: Pick<ChronumLoad, 'origin' | 'started_by_name'>): string {
  if (load.origin === 'schedule') return 'по расписанию';
  return load.started_by_name?.trim() || 'вручную';
}

export function shortHash(sha: string | null | undefined): string | null {
  return sha ? sha.slice(0, 8) : null;
}

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  live: 'живые данные — месяц открыт',
  'closed-snapshot': 'снимок закрытого месяца',
  'fot-final': 'окончательный отчёт ФОТ',
};

/** Откуда Хронум взял данные (у начисленного ФОТ). Неизвестное — `null`. */
export function sourceLabel(source: string | null | undefined): string | null {
  return source ? (SOURCE_LABEL[source] ?? null) : null;
}

export type ChronumStatusCounts = ChronumLoadCounts;

/** Сколько снимков с каким итогом. */
export function countStatuses(statuses: readonly string[]): ChronumStatusCounts {
  const c: ChronumStatusCounts = { ok: 0, not_available: 0, denied: 0, error: 0 };
  for (const s of statuses) {
    if (s === 'ok' || s === 'not_available' || s === 'denied' || s === 'error') c[s] += 1;
  }
  return c;
}

/** «3 загружено · 5 Хронум пока не отдаёт». */
export function countsText(c: ChronumStatusCounts): string {
  const parts: string[] = [];
  if (c.ok) parts.push(`${c.ok} загружено`);
  if (c.not_available) parts.push(`${c.not_available} Хронум пока не отдаёт`);
  if (c.denied) parts.push(`${c.denied} без прав`);
  if (c.error) parts.push(`${c.error} ${plural(c.error, ['сбой', 'сбоя', 'сбоев'])}`);
  return parts.join(' · ');
}

// ── Загрузки ───────────────────────────────────────────────────────────────

export function loadStatusLabel(status: ChronumLoadStatus | string): string {
  switch (status) {
    case 'running':
      return 'Идёт';
    case 'done':
      return 'Закончена';
    case 'failed':
      return 'Сбой записи';
    case 'interrupted':
      return 'Оборвалась';
    default:
      return 'Итог неизвестен';
  }
}

export function loadStatusTone(status: ChronumLoadStatus | string): ChronumTone {
  switch (status) {
    case 'running':
      return 'info';
    case 'done':
      return 'success';
    case 'failed':
      return 'danger';
    case 'interrupted':
      return 'warning';
    default:
      return 'muted';
  }
}

/** Загрузка не закончилась сама: сбой записи или обрыв перезапуском. */
export function loadStopped(load: Pick<ChronumLoad, 'status'>): boolean {
  return load.status === 'failed' || load.status === 'interrupted';
}

/** «3 из 8». */
export function loadProgressText(load: Pick<ChronumLoad, 'steps_done' | 'steps_total'>): string {
  return `${formatInt(Math.min(load.steps_done, load.steps_total))} из ${formatInt(load.steps_total)}`;
}

/** Секунд с начала загрузки по часам пользователя (не меньше нуля). */
export function loadElapsed(load: Pick<ChronumLoad, 'started_at'>, nowMs: number): number {
  const t = Date.parse(load.started_at);
  return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((nowMs - t) / 1000));
}

/** Снимки загрузки в порядке важности данных. */
export function sortLoadSnapshots(items: readonly ChronumSnapshotMeta[]): ChronumSnapshotMeta[] {
  return [...items].sort(
    (a, b) => feedOrder(feedKey(a.endpoint, a.basis)) - feedOrder(feedKey(b.endpoint, b.basis)),
  );
}

/** Страницы истории → загрузки без повторов (страницы сдвигаются, когда идёт новая). */
export function flattenLoads(
  pages: readonly { items: readonly ChronumLoadWithSnapshots[] }[],
): ChronumLoadWithSnapshots[] {
  const seen = new Set<string>();
  const out: ChronumLoadWithSnapshots[] = [];
  for (const p of pages) {
    for (const l of p.items) {
      if (seen.has(l.id)) continue;
      seen.add(l.id);
      out.push(l);
    }
  }
  return out;
}

// ── Итог «Обновить» ────────────────────────────────────────────────────────

export interface RefreshSummary {
  tone: ChronumTone;
  title: string;
  lines: string[];
}

/** Ключи данных итогов загрузки: у часов — с основанием из ответа. */
export function refreshResultKeys(res: {
  results: readonly Pick<ChronumLoadResult, 'endpoint' | 'basis'>[];
}): string[] {
  return res.results.map((r) => feedKey(r.endpoint, r.basis));
}

function labelsLower(keys: readonly string[]): string {
  return keys
    .map((k) => feedLabel(k))
    .map((l) => (l.startsWith('Блок') ? l : `${l.charAt(0).toLowerCase()}${l.slice(1)}`))
    .join(', ');
}

/**
 * Итог загрузки одной плашкой: сколько получено и чего нет. Оборванная
 * загрузка — с причиной и тем, что успело прийти.
 */
export function refreshSummary(
  load: Pick<ChronumLoad, 'period' | 'status' | 'steps_total' | 'message'>,
  results: readonly ChronumLoadResult[],
): RefreshSummary {
  const keys = refreshResultKeys({ results });
  const by = (status: string) => keys.filter((_, i) => results[i]?.status === status);
  const ok = by('ok');
  const missing = by('not_available');
  const denied = by('denied');
  const failed = by('error');
  const total = Math.max(load.steps_total, results.length);
  const month = isPeriod(load.period) ? periodLabelLower(load.period) : load.period;
  const lines: string[] = [];
  const stopped = loadStopped(load);
  if (stopped) {
    const reason = failureReason(load.message);
    lines.push(reason ? `${reason.replace(/\.$/, '')}.` : 'Загрузка остановилась на середине.');
  }
  if (missing.length) lines.push(`Хронум пока не отдаёт: ${labelsLower(missing)}.`);
  if (denied.length) lines.push(`Ключу не хватает прав: ${labelsLower(denied)}.`);
  if (failed.length) {
    const reasons = failed
      .map((k) => {
        const i = keys.indexOf(k);
        const reason = failureReason(results[i]?.message);
        return reason ? `${feedLabel(k)} — ${reason}` : feedLabel(k);
      })
      .join('; ');
    lines.push(`Не загрузилось: ${reasons}.`);
  }
  const tone: ChronumTone =
    load.status === 'failed' || failed.length
      ? 'danger'
      : stopped || denied.length
        ? 'warning'
        : ok.length === total && total > 0
          ? 'success'
          : 'info';
  return {
    tone,
    title: stopped
      ? `Загрузка за ${month} не закончилась: получено ${ok.length} из ${total}.`
      : `Загрузка за ${month}: получено ${ok.length} из ${total}.`,
    lines: lines.map(typo),
  };
}

// ── Категории ФОТ ──────────────────────────────────────────────────────────

/** Код «без категории» в своде (`by_category.unmapped`). */
export const UNMAPPED = 'unmapped';

/** Категории ФОТ БДР — как в `osnova-storage::econ_chronum::PAYROLL_CATEGORIES`. */
export const PAYROLL_CATEGORIES_FALLBACK: readonly ChronumPayrollCategory[] = [
  { code: 'workers', title: 'Основные рабочие' },
  { code: 'drivers_mechanics', title: 'Водители и механизаторы' },
  { code: 'aho', title: 'АХО' },
  { code: 'aho_itr', title: 'АХО ИТР' },
  { code: 'itr', title: 'ИТР' },
];

export function categoryTitle(
  code: string | null | undefined,
  categories: readonly ChronumPayrollCategory[],
): string {
  if (!code || code === UNMAPPED) return 'Без категории';
  return categories.find((c) => c.code === code)?.title ?? 'Другая категория';
}

export interface CategoryColumn {
  code: string;
  title: string;
}

/**
 * Колонки свода: категории ФОТ по порядку справочника, затем коды, которых
 * справочник не знает, и «без категории» — если такие часы есть.
 */
export function categoryColumns(
  categories: readonly ChronumPayrollCategory[],
  objects: readonly { by_category: Record<string, string> }[],
): CategoryColumn[] {
  const present = new Set<string>();
  for (const o of objects) {
    for (const [code, value] of Object.entries(o.by_category)) {
      if (isPositiveDecimal(value)) present.add(code);
    }
  }
  const cols: CategoryColumn[] = categories.map((c) => ({ code: c.code, title: c.title }));
  const known = new Set(cols.map((c) => c.code));
  for (const code of present) {
    if (!known.has(code) && code !== UNMAPPED) cols.push({ code, title: 'Другая категория' });
  }
  if (present.has(UNMAPPED)) cols.push({ code: UNMAPPED, title: 'Без категории' });
  return cols;
}

// ── Начисленное (свод ФОТ за правом `econ.payroll.read`) ───────────────────

/** Деньги: «1 023 600,00»; нет значения — «—». */
export function formatMoney(raw: string | null | undefined): string {
  const s = decimalOf(raw);
  if (s === null) return '—';
  const body = formatDecimal(s, 2) ?? s;
  // Копейки показываем всегда: «70 000» и «250,4» в колонке денег читаются как
  // обрезок суммы, а `formatDecimal` хвостовые нули снимает.
  const [int, frac = ''] = body.split(',');
  return `${int},${frac.padEnd(2, '0')}`;
}

/**
 * Деньги крупной цифрой: «1,52 млн ₽», «70 тыс. ₽», «250,40 ₽». Хвостовые нули
 * в сокращении не рисуем («70,0 тыс.» читается как точность, которой нет), а
 * суммы меньше тысячи показываем полностью, с копейками.
 */
export function formatMoneyShort(raw: string | null | undefined): string {
  const s = decimalOf(raw);
  if (s === null) return '—';
  const n = Number(s);
  if (!Number.isFinite(n)) return `${formatMoney(raw)}${NBSP}₽`;
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${formatDecimal((n / 1e6).toFixed(2), 2) ?? ''}${NBSP}млн${NBSP}₽`;
  if (abs >= 1e3) return `${formatDecimal((n / 1e3).toFixed(1), 1) ?? ''}${NBSP}тыс.${NBSP}₽`;
  return `${formatMoney(s)}${NBSP}₽`;
}

export interface PayrollTotals {
  accrued: string;
  hours: string;
  workersSumBySites: number;
  /** Деньги по коду категории ФОТ. */
  byCategory: Record<string, string>;
}

/** Итоги свода по объектам и центрам затрат. */
export function payrollTotals(objects: readonly ChronumPayrollObject[]): PayrollTotals {
  const codes = new Set<string>();
  for (const o of objects) Object.keys(o.by_category).forEach((c) => codes.add(c));
  const byCategory: Record<string, string> = {};
  for (const code of codes) byCategory[code] = addDecimals(objects.map((o) => o.by_category[code]));
  return {
    accrued: addDecimals(objects.map((o) => o.accrued)),
    hours: addDecimals(objects.map((o) => o.hours)),
    workersSumBySites: objects.reduce((sum, o) => sum + o.workers_sum_by_sites, 0),
    byCategory,
  };
}

/** Деньги, не попавшие в свод: вне объектов, с двумя привязками, без площадки. */
export function outsidePayroll(a: ChronumPayrollAnomalies): {
  unbound: string;
  conflicts: string;
  unattributed: string;
  total: string;
} {
  const unbound = addDecimals(a.unbound_sites.map((s) => s.accrued));
  const conflicts = addDecimals(a.conflicting_sites.map((s) => s.accrued));
  const unattributed = addDecimals(a.unattributed.map((u) => u.accrued));
  return {
    unbound,
    conflicts,
    unattributed,
    total: addDecimals([unbound, conflicts, unattributed]),
  };
}

/** Чего нет в суммах Хронума — по-русски. Неизвестный код отдаётся как есть. */
const NOT_INCLUDED: Readonly<Record<string, string>> = {
  insuranceContributions: 'страховые взносы',
  districtCoefficient: 'районный коэффициент',
  northernAllowance: 'северная надбавка',
  oneCAccruals: 'начисления из 1С',
  perDiem: 'суточные',
  vacationGross: 'отпускные',
  sickLeave: 'больничные',
  bonuses: 'премии',
};

export function notIncludedLabel(code: string): string {
  return NOT_INCLUDED[code] ?? code;
}

/** Почему деньги без площадки — словами Хронума. */
export function unattributedPayrollReason(reason: string): string {
  return (
    UNATTRIBUTED_REASON[reason] ??
    'Хронум не отнёс их к площадке по своей причине'
  );
}

// ── Должности ──────────────────────────────────────────────────────────────

/** Ключ поиска: нижний регистр, «ё» → «е», слова через один пробел. */
export function searchKey(text: string): string {
  return text
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Должность подходит под поиск: все слова — в названии, компаниях или комментарии. */
export function positionMatches(p: ChronumPosition, query: string): boolean {
  const words = searchKey(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;
  const hay = searchKey([p.name, ...p.companies, p.comment ?? ''].join(' '));
  return words.every((w) => hay.includes(w));
}

export interface PositionFilter {
  query: string;
  unmappedOnly: boolean;
  /**
   * Должности, которые остаются в списке «без категории», хотя категорию уже
   * выбрали: строка не исчезает из-под руки и фокус не теряется.
   */
  keep: ReadonlySet<string>;
}

export function filterPositions(
  items: readonly ChronumPosition[],
  { query, unmappedOnly, keep }: PositionFilter,
): ChronumPosition[] {
  return items.filter(
    (p) => positionMatches(p, query) && (!unmappedOnly || !p.payroll_category || keep.has(p.key)),
  );
}

export function sortPositions(items: readonly ChronumPosition[]): ChronumPosition[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

export function countUnmapped(items: readonly ChronumPosition[]): number {
  return items.filter((p) => !p.payroll_category).length;
}

// ── Журнал правок сопоставлений ────────────────────────────────────────────

const FIELD_LABEL: Readonly<Record<string, string>> = {
  payroll_category: 'Категория ФОТ',
  cost_center_code: 'Центр затрат',
  comment: 'Комментарий',
};

export interface RevisionChange {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** «Было → стало» записи журнала словами: категории — подписями, пусто — `null`. */
export function revisionChanges(
  rev: Pick<ChronumMapRevision, 'diff'>,
  categories: readonly ChronumPayrollCategory[],
): RevisionChange[] {
  const order = Object.keys(FIELD_LABEL);
  const text = (field: string, v: string | null): string | null => {
    if (v === null || v === '') return null;
    return field === 'payroll_category' ? categoryTitle(v, categories) : v;
  };
  return Object.entries(rev.diff)
    .sort(([a], [b]) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
    })
    .map(([field, v]) => ({
      field,
      label: FIELD_LABEL[field] ?? 'Другое поле',
      before: text(field, v.before),
      after: text(field, v.after),
    }));
}

/** «Изменено 20.10.2026, 14:02 — Мария Экономова»; сопоставления нет — `null`. */
export function updatedLine(
  updatedAt: string | null | undefined,
  by: string | null | undefined,
): string | null {
  if (!updatedAt) return null;
  const who = by?.trim();
  return `Изменено ${formatDateTime(updatedAt)}${who ? ` — ${who}` : ''}`;
}

// ── Площадки ───────────────────────────────────────────────────────────────

export type SiteState = 'conflict' | 'unbound' | 'project' | 'cost_center';

export function siteState(site: ChronumSite): SiteState {
  if (site.conflict) return 'conflict';
  if (site.unbound) return 'unbound';
  return site.projects.length > 0 ? 'project' : 'cost_center';
}

export function siteStateLabel(state: SiteState): string {
  switch (state) {
    case 'conflict':
      return 'Двойная привязка';
    case 'unbound':
      return 'Не привязана';
    case 'project':
      return 'Стройка';
    case 'cost_center':
      return 'Центр затрат';
  }
}

export function siteStateTone(state: SiteState): ChronumTone {
  switch (state) {
    case 'conflict':
      return 'danger';
    case 'unbound':
      return 'warning';
    default:
      return 'success';
  }
}

/** Как стройка получила площадку — коротко: «вручную», «по названию». */
export function bindingShort(binding: ChronumSiteBinding): string {
  return binding === 'auto' ? 'по названию' : 'вручную';
}

/** Как стройка получила площадку — полностью, для пояснений. */
export function bindingLabel(binding: ChronumSiteBinding): string {
  return binding === 'auto' ? 'подобрана по названию объекта' : 'выбрана вручную в профиле объекта';
}

/** Что делать с площадкой, которой нужно внимание; у привязанных — `null`. */
export function siteStateHint(site: ChronumSite): string | null {
  switch (siteState(site)) {
    case 'conflict': {
      const auto = site.projects.length > 0 && site.projects.every((p) => p.binding === 'auto');
      return typo(
        auto
          ? 'Часы площадки не попадают в свод, пока у неё две привязки. Стройка подобрала её по названию: снимите центр затрат или у Руководителя выберите площадки стройки вручную — без этой.'
          : 'Часы площадки не попадают в свод, пока у неё две привязки. Оставьте одну: снимите центр затрат или уберите площадку из профиля стройки у Руководителя.',
      );
    }
    case 'unbound':
      return typo(
        'Часы площадки идут «вне объектов». Укажите центр затрат или привяжите площадку к стройке у Руководителя: в профиле объекта — «Выбрать вручную».',
      );
    default:
      return null;
  }
}

export function siteNeedsAttention(site: ChronumSite): boolean {
  return site.conflict || site.unbound;
}

/** Сначала двойные привязки, потом непривязанные, дальше по названию. */
export function sortSites(items: readonly ChronumSite[]): ChronumSite[] {
  const rank = (s: ChronumSite) => (s.conflict ? 0 : s.unbound ? 1 : 2);
  return [...items].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'ru'));
}

export function siteCounts(items: readonly ChronumSite[]): {
  total: number;
  unbound: number;
  conflict: number;
} {
  return {
    total: items.length,
    unbound: items.filter((s) => s.unbound).length,
    conflict: items.filter((s) => s.conflict).length,
  };
}

/** Центры затрат январского БДР: ОГМ, «М Строй-К», АУП; с 03.11.2026 у «М Строй-К» отдельно лаборатория. */
export const DEFAULT_COST_CENTERS: readonly string[] = [
  'ОГМ',
  'АУП',
  'М Строй-К',
  'М Строй-К (лаборатория)',
];

/** Код центра затрат: без пробелов по краям, слова через один пробел (как на сервере). */
export function normalizeCostCenter(input: string): string {
  return input.trim().split(/\s+/).filter(Boolean).join(' ');
}

/** Ключ сравнения кодов без учёта регистра: «огм» и «ОГМ» — один центр затрат. */
export function costCenterKey(code: string): string {
  return normalizeCostCenter(code).toLowerCase();
}

/**
 * Подсказки кода: уже используемые коды (частые сверху), затем центры
 * январского БДР, которых ещё нет. Написания, различающиеся регистром, — одно.
 */
export function costCenterSuggestions(used: readonly ChronumCostCenter[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (code: string) => {
    const c = normalizeCostCenter(code);
    const k = c.toLowerCase();
    if (c && !seen.has(k)) {
      seen.add(k);
      out.push(c);
    }
  };
  [...used]
    .sort((a, b) => b.sites - a.sites || a.code.localeCompare(b.code, 'ru'))
    .forEach((c) => add(c.code));
  DEFAULT_COST_CENTERS.forEach(add);
  return out;
}

/**
 * Код уже заведён в другом написании — сервер сохранит заведённое: «огм» →
 * «ОГМ». Совпадает буква в букву или кода нет — `null`.
 */
export function existingSpelling(input: string, known: readonly string[]): string | null {
  const code = normalizeCostCenter(input);
  if (!code) return null;
  const key = code.toLowerCase();
  const found = known.find((k) => costCenterKey(k) === key);
  return found && normalizeCostCenter(found) !== code ? normalizeCostCenter(found) : null;
}

// ── Свод часов ─────────────────────────────────────────────────────────────

export function targetKey(t: ChronumTarget): string {
  return t.kind === 'project' ? `project:${t.id}` : `center:${t.code}`;
}

export function targetLabel(t: ChronumTarget): string {
  return t.kind === 'project' ? t.name : t.code;
}

/** «стройка · площадки подобраны по названию», «центр затрат». */
export function targetKindLabel(t: ChronumTarget): string {
  if (t.kind !== 'project') return 'центр затрат';
  return t.binding === 'auto' ? 'стройка, площадки подобраны по названию' : 'стройка';
}

/** Сначала стройки по названию, потом центры затрат по коду. */
export function sortHoursObjects<T extends { target: ChronumTarget }>(
  objects: readonly T[],
): T[] {
  const rank = (o: T) => (o.target.kind === 'project' ? 0 : 1);
  return [...objects].sort(
    (a, b) => rank(a) - rank(b) || targetLabel(a.target).localeCompare(targetLabel(b.target), 'ru'),
  );
}

/** Группа учитывается в маш.-часах (а не в сутках на объекте). */
export function isMachineHourUnit(unit: string): boolean {
  return unit !== 'day';
}

export interface HoursTotals {
  manHours: string;
  /** Сумма численности по площадкам — человек с двух площадок учтён дважды. */
  workersSumBySites: number;
  byCategory: Record<string, string>;
  /** Маш.-часы групп в маш.-часах (группы «в сутках» не суммируются). */
  machineHours: string;
}

/** Итог по объектам и центрам затрат — сложение строк без float. */
export function hoursTotals(objects: readonly ChronumHoursObject[]): HoursTotals {
  const codes = new Set<string>();
  for (const o of objects) Object.keys(o.by_category).forEach((c) => codes.add(c));
  const byCategory: Record<string, string> = {};
  for (const code of codes) byCategory[code] = addDecimals(objects.map((o) => o.by_category[code]));
  return {
    manHours: addDecimals(objects.map((o) => o.man_hours)),
    workersSumBySites: objects.reduce((sum, o) => sum + o.workers_sum_by_sites, 0),
    byCategory,
    machineHours: addDecimals(
      objects.flatMap((o) =>
        o.machine_hours.filter((m) => isMachineHourUnit(m.unit)).map((m) => m.machine_hours),
      ),
    ),
  };
}

/** Часы, которые не вошли в свод: без привязки, с двумя привязками, без площадки. */
export function outsideHours(a: ChronumHoursAnomalies): {
  unbound: string;
  conflicts: string;
  unattributed: string;
  total: string;
} {
  const unbound = addDecimals(a.unbound_sites.map((s) => s.hours));
  const conflicts = addDecimals(a.conflicting_sites.map((s) => s.hours));
  const unattributed = addDecimals(a.unattributed.map((u) => decimalOf(u.hours)));
  return {
    unbound,
    conflicts,
    unattributed,
    total: addDecimals([unbound, conflicts, unattributed]),
  };
}

/**
 * Пояснение к численности: это сумма по площадкам, а не люди, — и сколько
 * человек Хронум видел на нескольких площадках.
 */
export function headcountNote(h: ChronumHeadcount): string {
  const multi =
    h.multi_site_workers && h.multi_site_workers > 0
      ? ` По данным Хронума, на нескольких площадках за месяц работали ${people(h.multi_site_workers)} — в сумме они учтены не один раз.`
      : '';
  return typo(`${h.limitation}${multi}`);
}

/**
 * Аномалия свода. У площадок — переход к её привязке, у должностей без
 * категории — к списку должностей; остальное исправляется в самом Хронуме.
 */
export type HoursAnomaly = {
  id: string;
  tone: 'danger' | 'warning';
  text: string;
  /** Список мест — если их много, показываются первые. */
  items?: string[];
  more?: number;
} & (
  | { kind: 'conflict' | 'unbound'; siteId: string; siteName: string }
  | { kind: 'unmapped' }
  | { kind: 'unattributed' | 'marks' }
);

const UNATTRIBUTED_REASON: Readonly<Record<string, string>> = {
  no_site: 'Хронум не смог отнести их ни к одной площадке — отметок на терминалах нет',
  fixed_salary_no_hours: 'это оклад без отмеченных часов',
};

function targetPhrase(t: ChronumTarget): string {
  if (t.kind !== 'project') return `к центру затрат «${t.code}»`;
  return `к стройке «${t.name}» (${t.binding === 'auto' ? 'подобрана по названию' : 'выбрана вручную'})`;
}

/**
 * Аномалии свода понятными словами: что не так и что сделать. Сначала то, что
 * выбивает часы из свода (двойные привязки), потом остальное.
 */
export function hoursAnomalies(
  a: ChronumHoursAnomalies,
  { listLimit = 5 }: { listLimit?: number } = {},
): HoursAnomaly[] {
  const out: HoursAnomaly[] = [];
  for (const s of a.conflicting_sites) {
    const where = s.targets.length > 0 ? s.targets.map(targetPhrase).join(', и ') : 'дважды';
    out.push({
      id: `conflict:${s.site_id}`,
      kind: 'conflict',
      siteId: s.site_id,
      siteName: s.site_name,
      tone: 'danger',
      text: typo(
        `Площадка «${s.site_name}» привязана и ${where}. Её ${formatHours(s.hours)} ч (${people(s.workers)}) не вошли в свод — оставьте одну привязку.`,
      ),
    });
  }
  for (const s of a.unbound_sites) {
    out.push({
      id: `unbound:${s.site_id}`,
      kind: 'unbound',
      siteId: s.site_id,
      siteName: s.site_name,
      tone: 'warning',
      text: typo(
        `Площадка «${s.site_name}» — ${formatHours(s.hours)} ч вне объектов (${people(s.workers)}): привяжите её к центру затрат или к стройке у Руководителя.`,
      ),
    });
  }
  const unmapped = [...a.positions_without_category].sort(
    (x, y) => -compareDecimal(x.hours, y.hours),
  );
  if (unmapped.length > 0) {
    const n = unmapped.length;
    const hours = addDecimals(unmapped.map((p) => p.hours));
    out.push({
      id: 'positions-without-category',
      kind: 'unmapped',
      tone: 'warning',
      text: typo(
        `${n} ${plural(n, ['должность', 'должности', 'должностей'])} без категории ФОТ — ${formatHours(hours)} ч. Выберите категорию: пока эти часы числятся «без категории».`,
      ),
      items: unmapped
        .slice(0, listLimit)
        .map((p) => `${p.position_name} — ${formatHours(p.hours)} ч`),
      more: Math.max(0, n - listLimit),
    });
  }
  a.unattributed.forEach((u, i) => {
    const reason =
      UNATTRIBUTED_REASON[u.reason] ?? 'Хронум не отнёс их к площадке по своей причине';
    out.push({
      id: `unattributed:${i}`,
      kind: 'unattributed',
      tone: 'warning',
      text: typo(
        `Часы без площадки — ${formatHours(u.hours)} ч (${people(u.workers)}): ${reason}. Исправляется в Хронуме.`,
      ),
    });
  });
  if (a.marks_differ) {
    out.push({
      id: 'marks-differ',
      kind: 'marks',
      tone: 'warning',
      text: typo(
        `Табель и отметки терминалов расходятся на ${formatPercent(a.marks_gap_pct)} — больше допустимых 5${NBSP}%. Проверьте ручные правки табеля в Хронуме.`,
      ),
    });
  }
  return out;
}

/** Сравнение чисел-строк: -1, 0, 1. Не число считается нулём. */
export function compareDecimal(a: string, b: string): number {
  const x = parseScaled(a) ?? { digits: BigInt(0), scale: 0 };
  const y = parseScaled(b) ?? { digits: BigInt(0), scale: 0 };
  const scale = Math.max(x.scale, y.scale);
  const xv = x.digits * BigInt(10) ** BigInt(scale - x.scale);
  const yv = y.digits * BigInt(10) ** BigInt(scale - y.scale);
  return xv === yv ? 0 : xv > yv ? 1 : -1;
}

/** Сверка табеля с отметками, когда расхождение в норме или отметок нет. */
export function marksNote(a: ChronumHoursAnomalies): string | null {
  if (a.marks_differ) return null;
  if (a.marks_gap_pct === null) {
    return typo('Часов по отметкам за месяц нет — сверить табель с терминалами нельзя.');
  }
  return typo(`Табель и отметки сходятся: расхождение ${formatPercent(a.marks_gap_pct)}.`);
}

/** Замечания Хронума к данным месяца (ненулевые). Неизвестные — общей фразой. */
export function dataQualityNotes(dq: ChronumDataQuality): string[] {
  const out: string[] = [];
  const n = (v: number | null | undefined) => (typeof v === 'number' && v > 0 ? v : 0);
  if (n(dq.open_shifts_stale)) {
    const v = n(dq.open_shifts_stale);
    out.push(`${formatInt(v)} ${plural(v, ['смена', 'смены', 'смен'])} без отметки ухода`);
  }
  if (n(dq.multi_site_workers))
    out.push(`${people(n(dq.multi_site_workers))} на нескольких площадках`);
  if (n(dq.workers_without_position))
    out.push(`${people(n(dq.workers_without_position))} без должности`);
  const other = n(dq.other_notes);
  if (other > 0) out.push(`ещё ${other} ${plural(other, ['замечание', 'замечания', 'замечаний'])}`);
  return out;
}

export interface MonthState {
  closed: boolean;
  title: string;
  text: string;
  companies: string[];
}

function companyState(p: ChronumPeriodState): string {
  const name = p.company_name?.trim() || 'Компания без названия';
  const parts = [p.closed ? `закрыт${p.closed_at ? ` ${formatDate(p.closed_at)}` : ''}` : 'открыт'];
  if (p.fot_status === 'final') parts.push('отчёт ФОТ окончательный');
  else if (p.fot_status === 'draft') parts.push('отчёт ФОТ — черновик');
  if (p.payroll_excluded) parts.push('оплачен мимо Хронума');
  return `${name} — ${parts.join(', ')}`;
}

/** Закрыт ли месяц в Хронуме: предварительные данные или можно утверждать. */
export function monthState(
  hours: Pick<Extract<ChronumHours, { available: true }>, 'closed' | 'periods'>,
): MonthState {
  const companies = hours.periods.map(companyState);
  if (hours.closed) {
    return {
      closed: true,
      title: 'Месяц закрыт в Хронуме',
      text: typo('Часы этого снимка — для утверждения БДР.'),
      companies,
    };
  }
  return {
    closed: false,
    title: 'Месяц не закрыт в Хронуме — данные предварительные',
    text:
      hours.periods.length === 0
        ? typo('Хронум не сообщил, закрыт ли месяц, поэтому часы считаются предварительными.')
        : typo('Пока месяц открыт, Хронум пересчитывает табель — часы ещё могут измениться.'),
    companies,
  };
}

// ── История загрузок ───────────────────────────────────────────────────────

export type SnapshotChange = 'same' | 'changed';

/**
 * Изменились ли данные с прошлого удачного снимка тех же данных — по
 * контрольной сумме (прошлую присылает сервер). Сравнивать не с чем — `null`.
 */
export function snapshotChange(
  s: Pick<ChronumSnapshotMeta, 'sha256' | 'prev_sha256' | 'status'>,
): SnapshotChange | null {
  if (s.status !== 'ok' || !s.sha256 || !s.prev_sha256) return null;
  return s.sha256 === s.prev_sha256 ? 'same' : 'changed';
}

export interface SnapshotGroup {
  id: string;
  startedAt: string;
  origin: string;
  by: string;
  /** Месяц загрузки (`ГГГГ-ММ`). */
  period: string | null;
  snapshots: ChronumSnapshotMeta[];
  counts: ChronumStatusCounts;
}

/** Снимки одной загрузки идут подряд; дальше — уже другая загрузка. */
const LOAD_GAP_MS = 10 * 60 * 1000;

function time(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

function snapshotKey(s: ChronumSnapshotMeta): string {
  return `${feedKey(s.endpoint, s.params)}@${s.period ?? ''}`;
}

/**
 * Снимки → загрузки месяца по времени съёмки (до `load_id` у снимков).
 * Сейчас загрузки группирует сервер (`/snapshots` отдаёт загрузки со своими
 * снимками) — функция экраном не используется.
 */
export function groupLoads(items: readonly ChronumSnapshotMeta[], period: string): SnapshotGroup[] {
  const sorted = [...items].sort((a, b) => time(a.fetched_at) - time(b.fetched_at));
  const groups: ChronumSnapshotMeta[][] = [];
  let current: ChronumSnapshotMeta[] | null = null;
  for (const s of sorted) {
    const prev = current?.[current.length - 1];
    const startsNew =
      !current ||
      !prev ||
      s.endpoint === 'sites' ||
      s.origin !== prev.origin ||
      (s.fetched_by ?? '') !== (prev.fetched_by ?? '') ||
      time(s.fetched_at) - time(prev.fetched_at) > LOAD_GAP_MS ||
      current.some((x) => snapshotKey(x) === snapshotKey(s));
    if (startsNew || !current) {
      current = [s];
      groups.push(current);
    } else {
      current.push(s);
    }
  }
  return groups
    .map((snaps): SnapshotGroup => {
      const first = snaps[0];
      const loadPeriod =
        snaps.map((s) => periodFromDate(s.period)).find((p): p is string => p !== null) ?? null;
      return {
        id: first?.id ?? '',
        startedAt: first?.fetched_at ?? '',
        origin: first?.origin ?? 'manual',
        by: first ? originLabel(first) : 'вручную',
        period: loadPeriod,
        snapshots: sortLoadSnapshots(snaps),
        counts: countStatuses(snaps.map((s) => s.status)),
      };
    })
    .filter((g) => g.period === period)
    .sort((a, b) => time(b.startedAt) - time(a.startedAt));
}

/**
 * Изменились ли данные с прошлой удачной загрузки тех же данных за тот же
 * месяц — по контрольной сумме ответа, внутри одного списка снимков. Сейчас
 * прошлую сумму присылает сервер ([`snapshotChange`]) — функция экраном не
 * используется.
 */
export function snapshotChanges(
  items: readonly ChronumSnapshotMeta[],
): Map<string, SnapshotChange> {
  const sorted = [...items]
    .filter((s) => s.status === 'ok' && s.sha256)
    .sort((a, b) => time(a.fetched_at) - time(b.fetched_at));
  const last = new Map<string, string>();
  const out = new Map<string, SnapshotChange>();
  for (const s of sorted) {
    const key = snapshotKey(s);
    const prev = last.get(key);
    if (prev !== undefined && s.sha256) out.set(s.id, prev === s.sha256 ? 'same' : 'changed');
    if (s.sha256) last.set(key, s.sha256);
  }
  return out;
}

/**
 * Последний снимок данных (любой итог) за месяц из списка снимков. Сейчас
 * последний снимок каждой ручки присылает `status.last` — функция экраном не
 * используется.
 */
export function latestSnapshot(
  items: readonly ChronumSnapshotMeta[],
  key: string,
  period: string,
): ChronumSnapshotMeta | null {
  let best: ChronumSnapshotMeta | null = null;
  for (const s of items) {
    if (feedKey(s.endpoint, s.params) !== key || periodFromDate(s.period) !== period) continue;
    if (!best || time(s.fetched_at) > time(best.fetched_at)) best = s;
  }
  return best;
}

export interface EmptyHours {
  title: string;
  text: string;
  /** Есть смысл предложить «Обновить». */
  canRefresh: boolean;
}

/** Почему за месяц нет свода часов и что делать. */
export function emptyHoursReason(
  latest: Pick<ChronumSnapshotMeta, 'status' | 'fetched_at' | 'message'> | null,
  configured: boolean,
): EmptyHours {
  if (!configured) {
    return {
      title: 'Хронум не подключён',
      text: typo(
        'Администратор должен добавить на сервер ключ Экономиста — тогда часы начнут загружаться по кнопке «Обновить» и сами, по расписанию.',
      ),
      canRefresh: false,
    };
  }
  const when = latest ? formatDateTime(latest.fetched_at) : '';
  switch (latest?.status) {
    case 'not_available':
      return {
        title: 'Хронум пока не отдаёт часы',
        text: typo(
          `Выгрузку часов по должностям в Хронуме ещё делают. Последняя попытка — ${when}. Как только Хронум начнёт её отдавать, нажмите «Обновить».`,
        ),
        canRefresh: true,
      };
    case 'denied':
      return {
        title: 'Нет прав на часы',
        text: typo(
          `Ключу Экономиста в Хронуме не хватает прав на часы (попытка ${when}). Права выдаёт администратор Хронума.`,
        ),
        canRefresh: true,
      };
    case 'error': {
      const reason = failureReason(latest?.message);
      return {
        title: 'Часы не загрузились',
        text: typo(
          `Последняя попытка — ${when}${reason ? `: ${reason}` : ''}. Повторите «Обновить».`,
        ),
        canRefresh: true,
      };
    }
    default:
      return {
        title: 'Часов за этот месяц ещё нет',
        text: typo(
          'Нажмите «Обновить» — или дождитесь загрузки по расписанию: сама она берёт текущий и прошлый месяц.',
        ),
        canRefresh: true,
      };
  }
}
